import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarCheck, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Wand2, Clock, CalendarDays, Copy, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { refreshSidebarCounts } from "../lib/sidebarBus";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { useConfirm } from "../hooks/useConfirm";

const WD = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"]; // 0=Mon..6=Sun

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.replace(" ", "T"));
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${WD[(d.getDay() + 6) % 7]} ${dd}/${mm}/${d.getFullYear()}`;
}
function timeOf(iso) {
  return iso ? iso.slice(11, 16) : "—";
}

export default function ScheduleAudit() {
  const confirm = useConfirm();
  const [data, setData] = useState({ clients: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [onlyDiv, setOnlyDiv] = useState(true);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const unchecked = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/schedule-audit", { params: { only_divergent: onlyDiv } });
      setData(data);
      setSelected(new Set(data.clients.flatMap((c) => c.jobs.filter((j) => j.duplicate && !unchecked.current.has(j.job_id)).map((j) => j.job_id))));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  }, [onlyDiv]);
  useEffect(() => { load(); }, [load]);

  const divergentJobIds = useMemo(
    () => data.clients.flatMap((c) => c.jobs.filter((j) => (j.day_wrong || j.time_wrong) && !j.duplicate).map((j) => j.job_id)),
    [data],
  );

  function toggleSel(id) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) { n.delete(id); unchecked.current.add(id); } else { n.add(id); unchecked.current.delete(id); }
      return n;
    });
  }

  async function removeSelected() {
    const ids = [...selected];
    if (!ids.length) return;
    const ok = await confirm({
      title: `Apagar ${ids.length} casa(s) do sistema?`,
      description: "As casas marcadas serão apagadas definitivamente. Casas já finalizadas ou em andamento nunca são apagadas.",
      destructive: true,
      actionLabel: "Apagar selecionadas",
    });
    if (!ok) return;
    setApplying(true);
    try {
      const { data: r } = await api.post("/schedule-audit/remove", { job_ids: ids });
      toast.success(`${r.deleted} casa(s) apagada(s)`);
      refreshSidebarCounts();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao apagar");
    } finally {
      setApplying(false);
    }
  }

  async function apply(jobIds, label) {
    if (!jobIds.length) return;
    const ok = await confirm({
      title: `Corrigir ${jobIds.length} casa(s)?`,
      description: `${label} As casas serão movidas para o dia e horário cadastrados no cliente. Casas já finalizadas não são afetadas.`,
      actionLabel: "Corrigir agora",
    });
    if (!ok) return;
    setApplying(true);
    try {
      const { data: r } = await api.post("/schedule-audit/apply", { job_ids: jobIds });
      toast.success(`${r.updated} casa(s) corrigida(s)${r.skipped ? ` · ${r.skipped} ignorada(s)` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao corrigir");
    } finally {
      setApplying(false);
    }
  }

  const t = data.totals || {};

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="schedule-audit-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">Agenda</div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarCheck className="text-blue-600" /> Corrigir agenda</h1>
            <p className="text-sm text-slate-500 max-w-2xl">
              Comparo o dia e a hora de cada casa <b>agendada</b> com o que está cadastrado na aba <b>Agendamento</b> do cliente e corrijo as que estão fora. Também encontro <b>casas duplicadas</b> (ex.: duas casas de cliente mensal no mesmo mês) — você escolhe quais apagar. Casas já finalizadas não são alteradas.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setOnlyDiv((v) => !v)} data-testid="toggle-only-divergent"
              className={`px-3 py-2 rounded-lg border text-sm font-medium ${onlyDiv ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-white border-slate-200 text-slate-600"}`}>
              {onlyDiv ? "Só divergentes" : "Todas as casas"}
            </button>
            <button onClick={load} data-testid="audit-reload" className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50">
              <RefreshCw size={15} />
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={AlertTriangle} tone="amber" label="Casas fora do dia/hora" value={t.divergent_jobs ?? 0} testId="stat-divergent" />
          <StatCard icon={Copy} tone="red" label="Casas duplicadas" value={t.duplicate_jobs ?? 0} testId="stat-duplicates" />
          <StatCard icon={CalendarDays} tone="blue" label="Clientes listados" value={t.clients ?? 0} />
          <StatCard icon={Clock} tone="slate" label="Sem configuração de agenda" value={t.clients_without_config ?? 0} />
        </div>

        {divergentJobIds.length > 0 && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <span className="text-sm text-blue-900 font-medium">
              {divergentJobIds.length} casa(s) fora do dia/hora cadastrado.
            </span>
            <Button onClick={() => apply(divergentJobIds, "Correção em massa de todos os clientes divergentes.")} disabled={applying}
              data-testid="apply-all" className="bg-blue-600 hover:bg-blue-700">
              {applying ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Wand2 size={15} className="mr-1" />}
              Corrigir todas
            </Button>
          </div>
        )}

        {selected.size > 0 && (
          <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="duplicates-bar">
            <span className="text-sm text-red-900 font-medium">
              {selected.size} casa(s) marcada(s) para apagar. Confira e desmarque o que quiser manter.
            </span>
            <Button onClick={removeSelected} disabled={applying} data-testid="remove-selected" className="bg-red-600 hover:bg-red-700">
              {applying ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Trash2 size={15} className="mr-1" />}
              Apagar selecionadas ({selected.size})
            </Button>
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center text-slate-400 cm-card">Carregando…</div>
        ) : data.clients.length === 0 ? (
          <div className="p-16 text-center text-slate-500 cm-card" data-testid="audit-empty">
            <CheckCircle2 size={40} className="mx-auto mb-3 text-emerald-400" />
            {onlyDiv ? "Nenhuma casa fora do dia/hora nem duplicada. Agenda alinhada!" : "Nenhuma casa agendada encontrada."}
          </div>
        ) : (
          <div className="space-y-4">
            {data.clients.map((c) => (
              <ClientCard key={c.client_id} c={c} onApply={apply} applying={applying} selected={selected} onToggle={toggleSel} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function ClientCard({ c, onApply, applying, selected, onToggle }) {
  const clientDivIds = c.jobs.filter((j) => (j.day_wrong || j.time_wrong) && !j.duplicate).map((j) => j.job_id);
  return (
    <div className="cm-card overflow-hidden" data-testid={`audit-client-${c.client_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-100 bg-slate-50/60">
        <div>
          <div className="font-bold text-slate-800">{c.client_name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {c.has_config ? (
              <>Cadastrado: <b className="text-slate-700">{(c.target_weekdays || []).map((w) => WD[w]).join(", ") || "—"}</b> às <b className="text-slate-700">{c.target_time || "—"}</b> · {c.frequency || "—"}</>
            ) : (
              <span className="text-amber-700">⚠ Sem dia/hora cadastrado na aba Agendamento — não é possível corrigir automaticamente.</span>
            )}
          </div>
        </div>
        {clientDivIds.length > 0 && (
          <Button onClick={() => onApply(clientDivIds, `Cliente ${c.client_name}.`)} disabled={applying}
            data-testid={`apply-client-${c.client_id}`} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
            <Wand2 size={14} className="mr-1" /> Corrigir {clientDivIds.length}
          </Button>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {c.jobs.map((j) => {
          const ok = !j.day_wrong && !j.time_wrong && !j.duplicate;
          return (
            <div key={j.job_id} className={`flex flex-wrap items-center gap-3 p-3 text-sm ${selected.has(j.job_id) ? "bg-red-50/60" : ""}`} data-testid={`audit-job-${j.job_id}`}>
              <input type="checkbox" checked={selected.has(j.job_id)} onChange={() => onToggle(j.job_id)}
                className="w-4 h-4 accent-red-600" title="Marcar para apagar" data-testid={`audit-select-${j.job_id}`} />
              <div className={`w-2 h-2 rounded-full ${ok ? "bg-emerald-400" : "bg-red-400"}`} />
              <div className="flex-1 min-w-[220px]">
                <span className={j.day_wrong ? "text-red-600 font-semibold" : "text-slate-700"}>{fmtDate(j.current)}</span>
                <span className="mx-1 text-slate-300">·</span>
                <span className={j.time_wrong ? "text-red-600 font-semibold" : "text-slate-700"}>{j.current_time}</span>
              </div>
              {j.duplicate ? (
                <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 font-semibold inline-flex items-center gap-1" data-testid={`audit-dup-${j.job_id}`}>
                  <Copy size={12} /> Duplicada de {fmtDate(j.duplicate_of)}
                </span>
              ) : ok ? (
                <span className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1"><CheckCircle2 size={13} /> Correto</span>
              ) : (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-400">→</span>
                  <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 font-semibold">
                    {fmtDate(j.proposed)} · {j.proposed_time}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, tone, label, value, testId }) {
  const tones = {
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    slate: "bg-slate-50 text-slate-600 border-slate-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div className={`rounded-xl border p-4 flex items-center gap-3 ${tones[tone]}`} data-testid={testId}>
      <Icon size={22} />
      <div>
        <div className="text-2xl font-bold leading-none">{value}</div>
        <div className="text-xs font-medium mt-1 opacity-80">{label}</div>
      </div>
    </div>
  );
}
